<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title>
            Profile Information
        </template>
        <template #description>
            Update your account's profile information and email address.
        </template>
        <template #form>
            <!-- Profile Photo -->
            <div class="col-span-6 sm:col-span-4" v-if="$page.props.jetstream.managesProfilePhotos">
                <!-- Profile Photo File Input -->
                <input type="file" class="hidden" ref="photo" @change="updatePhotoPreview">
                <jet-label for="photo" value="Photo" />
                <!-- Current Profile Photo -->
                <div class="mt-2" v-show="! photoPreview">
                    <img :src="user.profile_photo_url" :alt="user.name" class="rounded-full h-20 w-20 object-cover">
                </div>
                <!-- New Profile Photo Preview -->
                <div class="mt-2" v-show="photoPreview">
                    <span class="block rounded-full w-20 h-20" :style="'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' + photoPreview + '\');'">
                    </span>
                </div>
                <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto">
                    Select A New Photo
                </jet-secondary-button>
                <jet-secondary-button type="button" class="mt-2" @click.prevent="deletePhoto" v-if="user.profile_photo_path">
                    Remove Photo
                </jet-secondary-button>
                <jet-input-error :message="form.errors.photo" class="mt-2" />
            </div>

            <!-- Team -->
            <div v-if="$page.props.settings.site_name == 'Racing League'" class="col-span-6 sm:col-span-4">
                <jet-label for="team" value="Team" />

                <button required name="team" id="dropdownteamsButton" @click="toggleDropdown" :class="[selectedTeam ? 'bg-rl-' + selectedTeam.value : (user.team ? 'bg-rl-' + user.team : ''), isDropdownOpen ? 'rounded-b-none' : '']" class="text-white font-medium rounded-lg text-sm px-4 py-2.5 text-center inline-flex items-center w-full mt-1 backdrop-blur h-[51px]" type="button">
                    <span class="inline-flex w-full justify-between" >
                        <!-- Show the selected team if the user has a team or if a team is selected -->
                        <img :src="getImagePath()" class="w-5"/>
                        <p class="capitalize my-auto" v-if="user.team || selectedTeam">{{ selectedTeam ? selectedTeam.name : user.team }}</p>

                        <!-- Show the default "Choose your team" message if the user has no team or selected team -->
                        <p v-else> Choose your team </p>

                        <svg class="w-5 h-5 ml-2 my-auto" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </span>
                </button>

                <!-- Dropdown menu -->
                <div :class="{'hidden': !isDropdownOpen}" id="dropdownteams" class="rounded-lg rounded-t-none shadow w-full bg-gray-900">
                    <ul @click="toggleDropdown" class="h-48 overflow-y-auto text-gray-700 dark:text-gray-200 rounded-md" aria-labelledby="dropdownteamsButton">
                        <li @click="selectedTeam=team" v-for="team in teams" :key="team.id">
                            <a href="#" class="flex items-center text-base px-4 py-2 hover:opacity-90 hover:text-white" :class="[team.background]">
                                <img class="w-6 h-6 mr-2" :src="team.icon" :alt="team.name + ' image'">
                                {{ team.name }}
                            </a>
                        </li>
                    </ul>
                </div>
                <jet-input-error :message="form.errors.team" class="mt-2" />
            </div>


            <!-- First Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="first_name" value="First Name" />
                <jet-input id="first_name" type="text" class="mt-1 block w-full" v-model="form.first_name" autocomplete="given-name" style="background-color: rgb(31 41 55);"/>
                <jet-input-error :message="form.errors.first_name" class="mt-2" />
            </div>
            <!-- Last Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="last_name" value="Last Name" />
                <jet-input id="last_name" type="text" class="mt-1 block w-full" v-model="form.last_name" autocomplete="family-name" style="background-color: rgb(31 41 55);"/>
                <jet-input-error :message="form.errors.last_name" class="mt-2" />
            </div>
            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="email" value="Email"/>
                <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email" style="background-color: rgb(31 41 55);"/>
                <jet-input-error :message="form.errors.email" class="mt-2" />
            </div>
            <!-- Username -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="username" value="Username (Displayed Publicly)" />
                <jet-input id="username" type="text" class="mt-1 block w-full" v-model="form.username" autocomplete="username" style="background-color: rgb(31 41 55);"/>
                <jet-input-error :message="form.errors.username" class="mt-2" />
            </div>
            <!-- Mobile Number -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="mobile" value="Mobile Number" />
                <jet-input id="mobile" type="text" class="mt-1 block w-full" v-model="form.mobile" autocomplete="tel" style="background-color: rgb(31 41 55);"/>
                <jet-input-error :message="form.errors.mobile" class="mt-2" />
            </div>
            <!-- Date of Birth -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="date_of_birth" value="Date of Birth" />
                <jet-date-picker id="date_of_birth" class="mt-1 block w-full" v-model="form.date_of_birth" :as-single="true" placeholder="Date of Birth" :formatter="format" required autocomplete="bday" />
                <jet-input-error :message="form.errors.date_of_birth" class="mt-2" />
            </div>
        </template>
        <template #actions>
            <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                Saved.
            </jet-action-message>
            <jet-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Save
            </jet-button>
        </template>
    </jet-form-section>
</template>
<script>
import JetButton from '@/Jetstream/Button.vue'
import JetFormSection from '@/Jetstream/FormSection.vue'
import JetInput from '@/Jetstream/Input.vue'
import JetInputError from '@/Jetstream/InputError.vue'
import JetLabel from '@/Jetstream/Label.vue'
import JetActionMessage from '@/Jetstream/ActionMessage.vue'
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue'
import JetDatePicker from '@/Jetstream/DatePicker.vue'

export default {
    components: {
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetDatePicker,
    },

    props: ['user'],

    data() {
        return {
            form: this.$inertia.form({
                _method: 'PUT',
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                email: this.user.email,
                username: this.user.username,
                mobile: this.user.mobile,
                date_of_birth: new Date(this.user.date_of_birth,).toLocaleDateString('en-GB'),
                photo: null,
                team: this.user.team
            }),

            teams: [
                { id: 1, name: 'Wales & The West', icon: '../images/racingleague/rl-wales.svg', value:"wales", background:"bg-rl-wales/70" ,foreground:"text-rl-wales/80" },
                { id: 2, name: 'London & The South', icon: '../images/racingleague/rl-london.svg', value:"london", background:"bg-rl-london/70" ,foreground:"text-rl-london/80"},
                { id: 3, name: 'Scotland', icon: '../images/racingleague/rl-scotland.svg', value:"scotland", background:"bg-rl-scotland/70" ,foreground:"text-rl-scotland/80"},
                { id: 4, name: 'The East', icon: '../images/racingleague/rl-east.svg', value:"east", background:"bg-rl-east/70" ,foreground:"text-rl-east/80"},
                { id: 5, name: 'Yorkshire', icon: '../images/racingleague/rl-yorkshire.svg', value:"yorkshire", background:"bg-rl-yorkshire/70" ,foreground:"text-rl-yorkshire/80"},
                { id: 6, name: 'The North', icon: '../images/racingleague/rl-north.svg', value:"north", background:"bg-[#1b2335] bg-opacity-70" ,foreground:"text-[#2b3b61]"},
                { id: 7, name: 'Ireland', icon: '../images/racingleague/rl-ireland.svg', value:"ireland", background:"bg-rl-ireland/70" ,foreground:"text-rl-ireland/80"},
            ],

            photoPreview: null,

            format: {
                date: 'DD/MM/YYYY',
                month: 'MMM'
            },

            isDropdownOpen: false,
        }
    },

    methods: {
        updateProfileInformation() {
            if (this.$refs.photo) {
                this.form.photo = this.$refs.photo.files[0]
            }

            this.form.team = this.selectedTeam ? this.selectedTeam.value : (this.user.team ? this.user.team : null);

            this.form.post(route('user-profile-information.update'), {
                errorBag: 'updateProfileInformation',
                preserveScroll: true,
                onSuccess: () => (this.clearPhotoFileInput()),
            });
        },

        getImagePath() {
            // Assuming 'siteName' contains the site name, you can replace it with the actual variable name holding the site name.
            const siteName = 'racingleague';
            const teamName = this.selectedTeam ? this.selectedTeam.value : this.user.team;

            // Construct the image asset path dynamically using template literals
            return `../images/${siteName.toLowerCase()}/rl-${teamName}.svg`;
        },

        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },

        selectNewPhoto() {
            this.$refs.photo.click();
        },

        updatePhotoPreview() {
            const photo = this.$refs.photo.files[0];

            if (!photo) return;

            const reader = new FileReader();

            reader.onload = (e) => {
                this.photoPreview = e.target.result;
            };

            reader.readAsDataURL(photo);
        },

        deletePhoto() {
            this.$inertia.delete(route('current-user-photo.destroy'), {
                preserveScroll: true,
                onSuccess: () => {
                    this.photoPreview = null;
                    this.clearPhotoFileInput();
                },
            });
        },

        clearPhotoFileInput() {
            if (this.$refs.photo?.value) {
                this.$refs.photo.value = null;
            }
        },
    },
}

</script>
